import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Phone, MapPin, Clock, Scissors, ChevronRight, CheckCircle, Send, Star, Instagram, MessageCircle } from "lucide-react";
import api from "../lib/api";

/* ─── Brand ─────────────────────────────────────────────────── */
const LOGO_URL = "/logo.jpg";
const GOOGLE_URL = "https://share.google/Sk5bt36UvYcvi05bM";
const HERO_IMG = "https://images.unsplash.com/photo-1768809248422-b5a82fef6b15?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const CRAFT_IMG = "https://images.pexels.com/photos/6765658/pexels-photo-6765658.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const MARQUEE_ITEMS = ["Custom Shirts","Suits & Coats","Sherwani","Kurta & Pathani","Blazers","Waistcoats","Trousers","Wedding Wear","Formal Wear","Indo-Western","Jodhpuri Suits","Perfect Fit Guaranteed"];

const SERVICES = [
  { name: "Shirts",            desc: "Perfectly fitted formal & casual shirts",        img: "https://customer-assets.emergentagent.com/job_e18c4530-3bbd-452e-9213-d9b384e9b01f/artifacts/yg4lfurs_IMG_3768.JPG.jpeg" },
  { name: "Suits & Coats",     desc: "Classic two-piece and three-piece suits",         img: "https://images.unsplash.com/photo-1700940994953-45a022ccd8bf?crop=entropy&cs=srgb&fm=jpg&q=85&w=600" },
  { name: "Sherwani",          desc: "Regal sherwani for weddings & celebrations",      img: "https://customer-assets.emergentagent.com/job_e18c4530-3bbd-452e-9213-d9b384e9b01f/artifacts/g8nky0c7_White%20%26%20Gold%20Sherwani.webp" },
  { name: "Kurta & Pathani",   desc: "Elegant ethnic wear with premium fabrics",        img: "https://images.pexels.com/photos/31371016/pexels-photo-31371016.png?auto=compress&cs=tinysrgb&dpr=2&h=650&w=600" },
  { name: "Blazers",           desc: "Sharp blazers with bespoke lining",               img: "https://images.unsplash.com/photo-1575042124694-d6fbe9082142?crop=entropy&cs=srgb&fm=jpg&q=85&w=600" },
  { name: "Trousers & Pants",  desc: "Tailored pants for every silhouette",             img: "https://images.pexels.com/photos/35070984/pexels-photo-35070984.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=600" },
];

const STORY_STEPS = [
  { num: "01", label: "Discover", title: "Your Vision, Our Canvas", body: "Every masterpiece starts with a conversation. Tell us your occasion, your style, your dream — and watch our expert tailors begin to see it too.", accent: "The brief" },
  { num: "02", label: "Measure",  title: "Precision in Every Inch", body: "Our master tailors take up to 22 precise measurements. Not just size — but posture, movement, and the subtle geometry of your body.", accent: "The craft" },
  { num: "03", label: "Create",   title: "Stitched with Intent",    body: "Thread by thread, seam by seam. Premium fabric cut to your measurements, hand-finished by artisans who have spent decades perfecting their craft.", accent: "The making" },
  { num: "04", label: "Deliver",  title: "Yours, Perfectly",        body: "We don't just deliver clothing — we deliver confidence. Every garment is pressed, finished, and handed to you ready to wear.", accent: "The moment" },
];

const REVIEWS = [
  { name: "Banti Saini",   rating: 5, text: "The best place to have your tailoring needs addressed. Truly excellent craftsmanship and attention to detail.", date: "2 months ago" },
  { name: "Monika Saini",  rating: 5, text: "Perfect in men's customisation — suits, shirts-pants, kurta-pajama. Every stitch was exactly right.", date: "3 months ago" },
  { name: "Mukhtar Ahmad", rating: 5, text: "Better fitting, excellent behaviour. The team is professional and very friendly. Highly recommended!", date: "1 month ago" },
  { name: "Rahul Sharma",  rating: 5, text: "Got my wedding sherwani made here. The quality and finishing exceeded all my expectations. Will come back.", date: "4 months ago" },
  { name: "Amit Verma",    rating: 5, text: "Brilliant work on my suit. The fit was absolutely perfect and delivery was exactly on time.", date: "5 months ago" },
  { name: "Harshit Sirohi", rating: 5, text: "Complete customised tailoring for men — they nailed every detail of my coat-pant order.", date: "6 months ago" },
];

const CLOTHING_OPTIONS = ["Shirt","Suit & Coat","Sherwani","Kurta","Pathani","Blazer","Waistcoat","Trousers & Pants","Other"];

/* FAQ — must mirror the JSON-LD FAQPage in /public/index.html for AEO/GEO */
const FAQS = [
  {
    q: "What services does VR The Darzi Studio offer?",
    a: "We offer complete bespoke men's tailoring in Gajraula, Amroha — custom suits, sherwani, kurta-pajama, pathani, blazers, waistcoats, shirts, trousers, modi jackets, wedding wear and police uniforms. Every garment is hand-cut and stitched to your exact measurements."
  },
  {
    q: "Where is VR The Darzi Studio located?",
    a: "We're on St Mary's Convent School Road, Gajraula, Sadullapur, Uttar Pradesh 244235, India — in the Amroha district. You can reach us at +91 98718 03080."
  },
  {
    q: "What are your working hours?",
    a: "Open all 7 days a week, 9:30 AM to 9:00 PM. No appointment needed for first measurement — but we recommend booking ahead for wedding and bulk orders."
  },
  {
    q: "How long does bespoke tailoring take?",
    a: "Standard garments (shirt, kurta, pant): 7–10 days. A 3-piece suit or sherwani: 12–18 days depending on fabric availability and design complexity. Rush delivery available for wedding orders."
  },
  {
    q: "Do you stitch wedding sherwani and groom wear?",
    a: "Yes — wedding sherwani, designer sherwani, indo-western groom wear and matching kurta-pajama for the wedding party are among our most-requested services. Our master tailors take 22+ precision measurements per garment to ensure a perfect fit on the big day."
  },
  {
    q: "Do you make police and government uniforms?",
    a: "Yes — official-spec police uniforms, security uniforms and other government uniforms for individual officers and bulk orders, with strict adherence to colour, badge placement and regulation cut."
  },
  {
    q: "Is VR The Darzi Studio the best tailor in Gajraula?",
    a: "We hold a 4.7★ rating with 31+ verified Google reviews from customers across Gajraula, Amroha and the surrounding region — among the highest-rated bespoke men's tailors in the area."
  },
];

/* ─── Hooks ──────────────────────────────────────────────────── */
function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

function useScrollProgress(containerRef) {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const windowH = window.innerHeight;
      const p = Math.max(0, Math.min(1, (windowH - rect.top) / (rect.height + windowH * 0.5)));
      setProgress(p);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [containerRef]);
  return progress;
}

/* ─── Components ─────────────────────────────────────────────── */
function Stars({ n = 5 }) {
  return <span className="flex gap-0.5">{Array.from({ length: n }).map((_, i) => <Star key={i} className="w-3.5 h-3.5 fill-[#D4AF37] text-[#D4AF37]" />)}</span>;
}

function Counter({ end, suffix = "", duration = 2000 }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !started.current) {
        started.current = true;
        const step = end / (duration / 16);
        let cur = 0;
        const tick = () => {
          cur = Math.min(cur + step, end);
          setVal(Math.floor(cur));
          if (cur < end) requestAnimationFrame(tick);
          else setVal(end);
        };
        requestAnimationFrame(tick);
      }
    }, { threshold: 0.3 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [end, duration]);
  return <span ref={ref}>{val.toLocaleString()}{suffix}</span>;
}

function RevealText({ children, delay = 0, className = "" }) {
  const [ref, inView] = useInView(0.2);
  return (
    <div ref={ref} className={`word-clip ${className}`}>
      <div className={`word-inner ${inView ? "revealed" : ""}`} style={{ animationDelay: `${delay}s` }}>
        {children}
      </div>
    </div>
  );
}

function ScrollReveal({ children, delay = 0, className = "" }) {
  const [ref, inView] = useInView(0.1);
  return (
    <div ref={ref} className={`scroll-reveal ${inView ? "visible" : ""} ${className}`}
      style={{ transitionDelay: `${delay}s` }}>
      {children}
    </div>
  );
}

/* Editorial-style section label: ( LABEL ) */
function SectionLabel({ children, className = "" }) {
  return (
    <p className={`editorial-label ${className}`}>
      <span className="paren">(</span>{children}<span className="paren">)</span>
    </p>
  );
}

/* FAQ accordion item — editorial style with number, plus/minus toggle */
function FaqItem({ q, a, num }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      itemScope
      itemProp="mainEntity"
      itemType="https://schema.org/Question"
      className={`border border-[#1a1a1a] rounded-sm transition-all duration-300 ${open ? "bg-[#0d0d0d] border-[rgba(212,175,55,0.25)]" : "bg-[#0a0a0a] hover:border-[rgba(212,175,55,0.15)]"}`}
    >
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full text-left px-5 sm:px-7 py-5 sm:py-6 flex items-center gap-4 sm:gap-6"
        aria-expanded={open}
      >
        <span className="numeral-massive text-[#D4AF37]/60 text-xl sm:text-2xl w-10 flex-shrink-0">{num}</span>
        <h3 itemProp="name" className="flex-1 font-display font-semibold text-white text-sm sm:text-base leading-snug">{q}</h3>
        <span className={`text-[#D4AF37] text-2xl font-light flex-shrink-0 transition-transform duration-300 ${open ? "rotate-45" : ""}`}>+</span>
      </button>
      <div
        itemScope
        itemProp="acceptedAnswer"
        itemType="https://schema.org/Answer"
        className={`grid transition-all duration-300 ${open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}
      >
        <div className="overflow-hidden">
          <p itemProp="text" className="px-5 sm:px-7 pb-6 sm:pb-7 pl-[68px] sm:pl-[88px] text-gray-400 leading-relaxed text-sm font-display font-light">{a}</p>
        </div>
      </div>
    </div>
  );
}

/* ─── Journey Map ────────────────────────────────────────────── */
function JourneyMap() {
  const containerRef = useRef(null);
  const pathRef = useRef(null);
  const carRef = useRef(null);
  const progress = useScrollProgress(containerRef);
  // Map sticky-engaged region (~p 0.35 → 0.85) to a 0→1 path-draw window
  const mapProgress = Math.max(0, Math.min(1, (progress - 0.35) / 0.5));
  const arrived = mapProgress > 0.95;

  // SVG journey path: Your Location → Main Market → Station Road → School Road → VR Studio
  const PATH_D = "M 40,220 L 180,220 Q 200,220 200,200 L 200,150 Q 200,130 220,130 L 340,130 Q 360,130 360,120 L 360,105 Q 360,90 380,90 L 490,90 Q 510,90 510,105 L 510,130 Q 510,150 530,150 L 580,150 Q 600,150 600,170 L 600,220 Q 600,240 620,240 L 820,240 Q 840,240 840,220 L 840,210";

  useEffect(() => {
    const pathEl = pathRef.current;
    const carEl = carRef.current;
    if (!pathEl || !carEl) return;
    const total = pathEl.getTotalLength();
    const current = Math.max(1, mapProgress * total);
    pathEl.style.strokeDasharray = total;
    pathEl.style.strokeDashoffset = total - current;
    const pt = pathEl.getPointAtLength(current);
    const ptPrev = pathEl.getPointAtLength(Math.max(0, current - 8));
    const angle = Math.atan2(pt.y - ptPrev.y, pt.x - ptPrev.x) * (180 / Math.PI);
    carEl.setAttribute("transform", `translate(${pt.x},${pt.y}) rotate(${angle}) scale(1.5)`);
  }, [mapProgress]);

  const WAYPOINTS = [
    { cx: 40,  cy: 220, label: "Your\nLocation", side: "top" },
    { cx: 200, cy: 200, label: "Main\nBazaar",   side: "left" },
    { cx: 360, cy: 120, label: "Railway\nStation", side: "top" },
    { cx: 600, cy: 165, label: "School\nRoad",   side: "right" },
    { cx: 840, cy: 210, label: "VR The\nDarzi Studio", side: "top", isDestination: true },
  ];

  return (
    <section className="py-0 bg-[#040404]">
      {/* Sticky container */}
      <div ref={containerRef} style={{ height: "200vh" }}>
        <div className="sticky top-0 h-screen flex flex-col items-center justify-center overflow-hidden px-4">
          {/* Section title */}
          <ScrollReveal className="text-center mb-8 z-10">
            <SectionLabel className="mb-3">FIND US</SectionLabel>
            <h2 className="display-tight text-white text-3xl md:text-5xl">
              <span>The Journey to </span>
              <span className="shine-text italic" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 600, letterSpacing: "-0.02em" }}>Our Studio.</span>
            </h2>
            <p className="text-gray-500 text-sm mt-3 max-w-md mx-auto font-display font-light">
              From wherever you are — scroll to follow the route to VR The Darzi Studio, Gajraula
            </p>
          </ScrollReveal>

          {/* Map SVG */}
          <div className="w-full max-w-4xl relative">
            <svg viewBox="0 0 880 350" className="w-full" style={{ filter: "drop-shadow(0 0 40px rgba(114,47,55,0.15))" }}>
              <defs>
                <radialGradient id="bgGrad" cx="50%" cy="50%" r="50%">
                  <stop offset="0%"   stopColor="#0d0a0a" />
                  <stop offset="100%" stopColor="#040404" />
                </radialGradient>
                <filter id="glow">
                  <feGaussianBlur stdDeviation="3" result="blur" />
                  <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
                </filter>
                {/* Luxury car gradients */}
                <linearGradient id="carBody" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%"   stopColor="#F8E29A" />
                  <stop offset="40%"  stopColor="#D4AF37" />
                  <stop offset="100%" stopColor="#7A5C12" />
                </linearGradient>
                <linearGradient id="carHighlight" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%"   stopColor="#FFFFFF" stopOpacity="0.7" />
                  <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
                </linearGradient>
                <linearGradient id="beamGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%"   stopColor="#FFF7D1" stopOpacity="0.85" />
                  <stop offset="100%" stopColor="#FFF7D1" stopOpacity="0" />
                </linearGradient>
                <radialGradient id="headlightGlow" cx="50%" cy="50%" r="50%">
                  <stop offset="0%"  stopColor="#FFF7D1" stopOpacity="1" />
                  <stop offset="60%" stopColor="#FFD66B" stopOpacity="0.4" />
                  <stop offset="100%" stopColor="#FFD66B" stopOpacity="0" />
                </radialGradient>
                <filter id="carShadow" x="-50%" y="-50%" width="200%" height="200%">
                  <feGaussianBlur in="SourceGraphic" stdDeviation="1.2" />
                </filter>
                <filter id="carGlow">
                  <feGaussianBlur stdDeviation="0.6" result="b" />
                  <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
                </filter>
              </defs>

              {/* Map background */}
              <rect width="880" height="350" fill="url(#bgGrad)" rx="8" />

              {/* City blocks */}
              {[
                [20,20,100,80],[140,20,80,80],[240,20,100,80],[360,20,60,60],[440,20,120,60],[590,20,80,80],[690,20,160,80],
                [20,280,100,50],[140,280,80,50],[240,280,100,50],[360,280,60,50],[440,280,80,50],[560,280,100,50],[700,280,160,50],
                [20,110,140,80],[680,110,180,100],
              ].map(([x,y,w,h],i) => <rect key={i} x={x} y={y} width={w} height={h} fill="#0a0808" stroke="#111" strokeWidth="0.5" rx="2" />)}

              {/* Roads - horizontal */}
              <rect x="0" y="210" width="880" height="20" fill="#111" />
              <rect x="0" y="120" width="360" height="14" fill="#0e0e0e" />
              <rect x="440" y="120" width="440" height="14" fill="#0e0e0e" />
              <rect x="0" y="80" width="880" height="8" fill="#0c0c0c" />

              {/* Roads - vertical */}
              <rect x="190" y="0" width="20" height="350" fill="#111" />
              <rect x="350" y="0" width="16" height="350" fill="#0e0e0e" />
              <rect x="590" y="0" width="20" height="350" fill="#111" />
              <rect x="790" y="0" width="16" height="350" fill="#0e0e0e" />

              {/* Lane dashes on main road */}
              {Array.from({ length: 22 }).map((_, i) => <rect key={i} x={i * 42} y="219" width="22" height="2" fill="#1a1a1a" />)}

              {/* Road labels */}
              {[
                [440, 207, "AMROHA ROAD"], [440, 117, "STATION ROAD"], [100, 71, "COLONY ROAD"],
                [188, 180, "MARKET\nROAD"], [598, 180, "SCHOOL\nROAD"],
              ].map(([x, y, t], i) => (
                <text key={i} x={x} y={y} textAnchor="middle" fontSize="7" fill="#222" fontFamily="monospace" letterSpacing="2">
                  {String(t).split("\n").map((l, j) => <tspan key={j} x={x} dy={j ? "9" : "0"}>{l}</tspan>)}
                </text>
              ))}

              {/* Animated journey path */}
              <path
                d={PATH_D}
                fill="none"
                stroke="#2a1a0a"
                strokeWidth="6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                ref={pathRef}
                d={PATH_D}
                fill="none"
                stroke="#D4AF37"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray="0"
                strokeDashoffset="0"
                filter="url(#glow)"
                style={{ transition: "stroke-dashoffset 0.05s linear" }}
              />

              {/* Waypoint dots */}
              {WAYPOINTS.map((wp, i) => (
                <g key={i}>
                  {wp.isDestination ? (
                    <>
                      {arrived && <circle cx={wp.cx} cy={wp.cy} r="16" fill="none" stroke="#D4AF37" strokeWidth="1" opacity="0.3" style={{ animation: "mapPulseOuter 2s ease-in-out infinite" }} />}
                      {arrived && <circle cx={wp.cx} cy={wp.cy} r="10" fill="none" stroke="#722F37" strokeWidth="1.5" opacity="0.6" style={{ animation: "mapPulse 2s ease-in-out infinite" }} />}
                      <circle cx={wp.cx} cy={wp.cy} r="7" fill={arrived ? "#D4AF37" : "#333"} stroke={arrived ? "#D4AF37" : "#444"} strokeWidth="1.5" style={{ transition: "fill 0.5s" }} />
                      <text x={wp.cx} y={wp.cy - 20} textAnchor="middle" fontSize="8" fill={arrived ? "#D4AF37" : "#444"} fontFamily="Georgia, serif" fontWeight="bold" style={{ transition: "fill 0.5s" }}>
                        {wp.label.split("\n").map((l, j) => <tspan key={j} x={wp.cx} dy={j ? "10" : "0"}>{l}</tspan>)}
                      </text>
                    </>
                  ) : (
                    <>
                      <circle cx={wp.cx} cy={wp.cy} r="4" fill="#333" stroke="#444" strokeWidth="1" />
                      <text x={wp.side === "left" ? wp.cx - 10 : wp.cx} y={wp.side === "top" ? wp.cy - 10 : wp.cy + 16}
                        textAnchor={wp.side === "left" ? "end" : "middle"} fontSize="7" fill="#333" fontFamily="monospace">
                        {wp.label.split("\n").map((l, j) => <tspan key={j} x={wp.side === "left" ? wp.cx - 10 : wp.cx} dy={j ? "9" : "0"}>{l}</tspan>)}
                      </text>
                    </>
                  )}
                </g>
              ))}

              {/* Luxury car icon — premium top-down sedan */}
              <g ref={carRef} transform="translate(40,220) rotate(0) scale(1.5)">
                {/* Soft drop shadow on road */}
                <ellipse cx="0" cy="0.8" rx="11" ry="5.2" fill="rgba(0,0,0,0.55)" filter="url(#carShadow)" />

                {/* Headlight beam cones (project forward = +X) */}
                <path d="M 11.5,-3.5 L 28,-9 L 30,-5 L 11.5,-2.5 Z" fill="url(#beamGrad)" opacity="0.55" />
                <path d="M 11.5,3.5 L 28,9 L 30,5 L 11.5,2.5 Z" fill="url(#beamGrad)" opacity="0.55" />

                {/* Wheels (under body, peek out the sides) */}
                <rect x="-8.5" y="-6.6" width="4.5" height="2" rx="0.7" fill="#0e0e0e" stroke="#2a2a2a" strokeWidth="0.2" />
                <rect x="5.5"  y="-6.6" width="4.5" height="2" rx="0.7" fill="#0e0e0e" stroke="#2a2a2a" strokeWidth="0.2" />
                <rect x="-8.5" y="4.6"  width="4.5" height="2" rx="0.7" fill="#0e0e0e" stroke="#2a2a2a" strokeWidth="0.2" />
                <rect x="5.5"  y="4.6"  width="4.5" height="2" rx="0.7" fill="#0e0e0e" stroke="#2a2a2a" strokeWidth="0.2" />

                {/* Main body — rounded sedan silhouette with tapered nose */}
                <path
                  d="M -10,-5.2 Q -11.2,-5.2 -11.2,-3.8 L -11.2,3.8 Q -11.2,5.2 -10,5.2 L 9,5.2 Q 11,5.2 12,3.4 L 12.2,-3.4 Q 11,-5.2 9,-5.2 Z"
                  fill="url(#carBody)"
                  stroke="#7A5C12"
                  strokeWidth="0.35"
                  filter="url(#carGlow)"
                />

                {/* Top body highlight gloss */}
                <path
                  d="M -9.5,-4.7 Q -10.5,-4.7 -10.5,-3.7 L -10.5,-0.5 L 11.4,-0.5 L 11.4,-3.1 Q 10.5,-4.7 9,-4.7 Z"
                  fill="url(#carHighlight)"
                  opacity="0.35"
                />

                {/* Rear window */}
                <path d="M -8.5,-3.4 L -4.2,-3.8 L -4.2,3.8 L -8.5,3.4 Z" fill="#050505" opacity="0.85" />
                {/* Front windshield */}
                <path d="M -3.5,-3.8 L 3.5,-3.4 L 3.5,3.4 L -3.5,3.8 Z" fill="#050505" opacity="0.78" />
                {/* Glass reflection sliver */}
                <path d="M -3.2,-3.5 L 3,-3.1 L 3,-2.4 L -3.2,-2.8 Z" fill="#ffffff" opacity="0.18" />

                {/* Center pillar / roof line */}
                <line x1="-3.8" y1="-3.6" x2="-3.8" y2="3.6" stroke="#5C460E" strokeWidth="0.25" opacity="0.7" />
                <line x1="-4.1" y1="0" x2="11.5" y2="0" stroke="#F8E29A" strokeWidth="0.18" opacity="0.4" />

                {/* Brake lights (rear) */}
                <rect x="-11" y="-4.2" width="1.1" height="2.2" rx="0.35" fill="#ff3b3b" opacity="0.95" />
                <rect x="-11" y="2"    width="1.1" height="2.2" rx="0.35" fill="#ff3b3b" opacity="0.95" />

                {/* Headlights (front) */}
                <circle cx="11.4" cy="-3.3" r="2.0" fill="url(#headlightGlow)" />
                <circle cx="11.4" cy="3.3"  r="2.0" fill="url(#headlightGlow)" />
                <circle cx="11.6" cy="-3.3" r="0.75" fill="#FFF7D1" />
                <circle cx="11.6" cy="3.3"  r="0.75" fill="#FFF7D1" />

                {/* Tiny VR emblem on hood */}
                <circle cx="7.5" cy="0" r="0.9" fill="#7A5C12" stroke="#F8E29A" strokeWidth="0.2" />
                <text x="7.5" y="0.4" textAnchor="middle" fontSize="1.2" fill="#F8E29A" fontFamily="Georgia, serif" fontWeight="bold">VR</text>
              </g>
            </svg>

            {/* Arrived info card */}
            {arrived && (
              <div className="absolute left-1/2 -translate-x-1/2 -bottom-2 md:left-auto md:translate-x-0 md:right-8 md:bottom-auto md:top-1/2 md:-translate-y-1/2 bg-[#0f0f0f] border border-[#D4AF37]/30 rounded-sm p-3 md:p-4 shadow-2xl animate-[fadeIn_0.6s_ease_forwards] w-[88%] max-w-xs md:w-52">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-2 h-2 rounded-full bg-[#D4AF37] animate-pulse" />
                  <span className="text-[#D4AF37] text-xs font-bold uppercase tracking-wider">Arrived</span>
                </div>
                <p style={{ fontFamily: "Georgia, serif" }} className="text-white font-bold text-sm mb-1">VR The Darzi Studio</p>
                <p className="text-gray-500 text-xs">St Mary's Convent School Road, Gajraula</p>
                <div className="mt-2 md:mt-3 pt-2 md:pt-3 border-t border-white/5 space-y-1.5">
                  <div className="flex items-center gap-1.5 text-xs text-gray-400"><Clock className="w-3 h-3 text-[#D4AF37]" /> 9:30 AM – 9:00 PM</div>
                  <div className="flex items-center gap-1.5 text-xs text-gray-400"><Phone className="w-3 h-3 text-[#D4AF37]" /> +91 98718 03080</div>
                </div>
              </div>
            )}
          </div>

          {/* Progress indicator */}
          <div className="mt-6 flex items-center gap-3">
            <div className="w-32 h-1 bg-white/5 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#722F37] to-[#D4AF37] rounded-full transition-all duration-100" style={{ width: `${mapProgress * 100}%` }} />
            </div>
            <span className="text-xs text-gray-600">
              {arrived ? "You've arrived!" : "Keep scrolling..."}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Sticky Story Process ───────────────────────────────────── */
function StoryProcess() {
  const containerRef = useRef(null);
  const progress = useScrollProgress(containerRef);
  // Map sticky-engaged region (~p 0.18 → 0.92) to 4 even steps
  const adj = Math.max(0, Math.min(1, (progress - 0.18) / 0.74));
  const activeStep = Math.min(3, Math.floor(adj * 4));

  return (
    <section className="bg-[#060606]">
      {/* Mobile: simple stacked cards (no sticky scroll) */}
      <div className="lg:hidden px-4 py-20">
        <ScrollReveal className="text-center mb-12">
          <SectionLabel className="mb-4">PROCESS</SectionLabel>
          <h2 className="display-tight text-white text-4xl sm:text-5xl">
            <span className="block">A Story in</span>
            <span className="block shine-text italic" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 600, letterSpacing: "-0.02em" }}>Four Acts.</span>
          </h2>
        </ScrollReveal>
        <div className="space-y-4 max-w-md mx-auto">
          {STORY_STEPS.map((s, i) => (
            <ScrollReveal key={i} delay={i * 0.08}>
              <div className="bg-[#0f0f0f] border border-[#1a1a1a] rounded-sm p-5 sm:p-6 relative overflow-hidden">
                <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#722F37] to-[#D4AF37]" />
                <div className="numeral-massive absolute top-2 right-4 text-white/[0.04] text-7xl select-none pointer-events-none">{s.num}</div>
                <div className="flex items-center gap-3 mb-2">
                  <span className="numeral-massive text-2xl text-[#D4AF37]">{s.num}</span>
                  <SectionLabel className="!text-[10px] !tracking-[0.28em]">{s.label}</SectionLabel>
                </div>
                <h3 className="display-tight text-white text-xl sm:text-2xl mb-3">{s.title}</h3>
                <p className="text-gray-400 leading-relaxed text-sm font-display font-light">{s.body}</p>
              </div>
            </ScrollReveal>
          ))}
        </div>
      </div>

      {/* Desktop: sticky scroll storytelling */}
      <div ref={containerRef} className="hidden lg:block" style={{ height: "400vh" }}>
        <div className="sticky top-0 h-screen flex items-center overflow-hidden">
          <div className="w-full max-w-7xl mx-auto px-4 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">

            {/* Left: Step nav */}
            <div className="space-y-1">
              <ScrollReveal>
                <SectionLabel className="mb-6">PROCESS</SectionLabel>
                <h2 className="display-tight text-white mb-10" style={{ fontSize: "clamp(40px, 5vw, 80px)" }}>
                  <span className="block">A Story in</span>
                  <span className="block shine-text italic" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 600, letterSpacing: "-0.02em" }}>Four Acts.</span>
                </h2>
              </ScrollReveal>
              {STORY_STEPS.map((s, i) => (
                <button key={i} onClick={() => {}} className="w-full text-left group">
                  <div className={`flex items-center gap-4 py-4 px-4 rounded-sm transition-all duration-500 ${activeStep === i ? "bg-[rgba(114,47,55,0.12)] border border-[rgba(114,47,55,0.3)]" : "border border-transparent"}`}>
                    <span className={`numeral-massive text-3xl transition-colors duration-500 w-12 flex-shrink-0 ${activeStep === i ? "text-[#D4AF37]" : "text-white/10"}`}>{s.num}</span>
                    <div>
                      <p className={`text-[10px] uppercase tracking-[0.28em] mb-0.5 transition-colors duration-500 font-display font-medium ${activeStep === i ? "text-[#D4AF37]" : "text-gray-700"}`}>{s.label}</p>
                      <p className={`font-display font-semibold text-base transition-colors duration-500 ${activeStep === i ? "text-white" : "text-gray-700"}`}>{s.title}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* Right: Active content panel */}
            <div className="relative h-80 lg:h-[420px]">
              {STORY_STEPS.map((s, i) => (
                <div key={i} className={`absolute inset-0 flex flex-col justify-center transition-all duration-700 ${activeStep === i ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8 pointer-events-none"}`}>
                  <div className="bg-[#0f0f0f] border border-[#1a1a1a] rounded-sm p-8 relative overflow-hidden">
                    <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#722F37] to-[#D4AF37]" />
                    <div className="numeral-massive text-[100px] text-white/[0.04] absolute top-4 right-6 select-none pointer-events-none">{s.num}</div>
                    <SectionLabel className="mb-3 !text-[10px]">{s.accent}</SectionLabel>
                    <h3 className="display-tight text-white text-3xl md:text-4xl mb-4">{s.title}</h3>
                    <p className="text-gray-400 leading-relaxed text-sm md:text-base font-display font-light">{s.body}</p>
                  </div>
                </div>
              ))}
            </div>

          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Booking Form ───────────────────────────────────────────── */
function BookingForm() {
  const emptyItem = { clothing_type: "", message: "" };
  const [form, setForm] = useState({ name: "", phone: "", address: "", items: [{ ...emptyItem }] });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const onChange = (e) => { setForm(f => ({ ...f, [e.target.name]: e.target.value })); setError(""); };
  const onItemChange = (idx, field, value) => {
    setForm(f => ({ ...f, items: f.items.map((it, i) => i === idx ? { ...it, [field]: value } : it) }));
    setError("");
  };
  const addItem = () => setForm(f => ({ ...f, items: [...f.items, { ...emptyItem }] }));
  const removeItem = (idx) => setForm(f => ({ ...f, items: f.items.length === 1 ? f.items : f.items.filter((_, i) => i !== idx) }));
  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) { setError("Please fill all required fields."); return; }
    if (form.items.some(it => !it.clothing_type)) { setError("Please select a clothing type for every item."); return; }
    setSubmitting(true);
    try { await api.post("/inquiries", { name: form.name, phone: form.phone, address: form.address, items: form.items }); setSubmitted(true); } catch { setError("Something went wrong. Please call us directly."); }
    setSubmitting(false);
  };

  return (
    <section id="book" className="py-20 sm:py-24 px-4 bg-[#060606] border-t border-white/5">
      <div className="max-w-6xl mx-auto">
        <ScrollReveal className="text-center mb-12 sm:mb-14">
          <SectionLabel className="mb-5">CONTACT</SectionLabel>
          <h2 className="display-tight text-white" style={{ fontSize: "clamp(36px, 6vw, 80px)" }}>
            <span className="block">Book Your</span>
            <span className="block shine-text italic" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 600, letterSpacing: "-0.02em" }}>Appointment.</span>
          </h2>
          <div className="w-20 h-[1px] bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent mx-auto mt-6 mb-5" />
          <p className="text-gray-500 text-sm sm:text-base max-w-md mx-auto font-display font-light">Fill your details below. We call you within hours to confirm.</p>
        </ScrollReveal>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          <ScrollReveal delay={0.1} className="lg:col-span-3">
            {submitted ? (
              <div data-testid="inquiry-success" className="bg-[#0f0f0f] border border-[#D4AF37]/20 rounded-sm p-12 flex flex-col items-center text-center min-h-[380px] justify-center">
                <div className="w-16 h-16 rounded-full bg-[#722F37]/20 flex items-center justify-center mb-5">
                  <CheckCircle className="w-8 h-8 text-[#D4AF37]" />
                </div>
                <h3 style={{ fontFamily: "Georgia, serif" }} className="text-2xl font-bold text-white mb-3">Request Received!</h3>
                <p className="text-gray-400 text-sm mb-6 max-w-xs">Thank you, <span className="text-[#D4AF37] font-semibold">{form.name}</span>. We'll call you at <span className="text-[#D4AF37] font-semibold">{form.phone}</span> shortly.</p>
                <button onClick={() => { setSubmitted(false); setForm({ name: "", phone: "", address: "", items: [{ ...emptyItem }] }); }} className="text-xs text-gray-600 hover:text-[#D4AF37] underline transition-colors">Submit another enquiry</button>
              </div>
            ) : (
              <form data-testid="inquiry-form" onSubmit={onSubmit} className="bg-[#0f0f0f] border border-[#1a1a1a] rounded-sm p-8 space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs text-gray-600 uppercase tracking-widest mb-2">Full Name <span className="text-[#722F37]">*</span></label>
                    <input data-testid="inquiry-name" name="name" value={form.name} onChange={onChange} placeholder="Your full name" className="w-full bg-[#080808] border border-[#222] rounded-sm px-4 py-3 text-white text-sm placeholder-gray-700 focus:outline-none focus:border-[#D4AF37]/40 transition-colors" required />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 uppercase tracking-widest mb-2">Phone <span className="text-[#722F37]">*</span></label>
                    <input data-testid="inquiry-phone" name="phone" value={form.phone} onChange={onChange} placeholder="+91 98765 43210" className="w-full bg-[#080808] border border-[#222] rounded-sm px-4 py-3 text-white text-sm placeholder-gray-700 focus:outline-none focus:border-[#D4AF37]/40 transition-colors" required />
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-gray-600 uppercase tracking-widest mb-2">Address <span className="text-gray-700">(optional)</span></label>
                  <input data-testid="inquiry-address" name="address" value={form.address} onChange={onChange} placeholder="House / street / locality, Gajraula" className="w-full bg-[#080808] border border-[#222] rounded-sm px-4 py-3 text-white text-sm placeholder-gray-700 focus:outline-none focus:border-[#D4AF37]/40 transition-colors" />
                </div>
                <div className="space-y-4">
                  {form.items.map((item, idx) => (
                    <div
                      key={idx}
                      data-testid={`inquiry-item-${idx}`}
                      className="bg-[#080808] border border-[#1a1a1a] rounded-sm p-4 space-y-4 relative"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] text-[#D4AF37] uppercase tracking-widest font-semibold">
                          Item {idx + 1}
                        </span>
                        {form.items.length > 1 && (
                          <button
                            type="button"
                            data-testid={`inquiry-item-remove-${idx}`}
                            onClick={() => removeItem(idx)}
                            className="text-[10px] text-gray-500 hover:text-[#722F37] uppercase tracking-widest transition-colors"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 uppercase tracking-widest mb-2">Clothing Type <span className="text-[#722F37]">*</span></label>
                        <select data-testid={`inquiry-clothing-type-${idx}`} value={item.clothing_type} onChange={e => onItemChange(idx, "clothing_type", e.target.value)} className="w-full bg-[#060606] border border-[#222] rounded-sm px-4 py-3 text-sm text-white focus:outline-none focus:border-[#D4AF37]/40 transition-colors cursor-pointer" required>
                          <option value="" disabled>Select clothing type...</option>
                          {CLOTHING_OPTIONS.map(o => <option key={o} value={o} className="bg-[#111]">{o}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 uppercase tracking-widest mb-2">Requirements <span className="text-gray-700">(optional)</span></label>
                        <textarea data-testid={`inquiry-message-${idx}`} value={item.message} onChange={e => onItemChange(idx, "message", e.target.value)} placeholder="Fabric preference, occasion, special requirements..." rows={3} className="w-full bg-[#060606] border border-[#222] rounded-sm px-4 py-3 text-white text-sm placeholder-gray-700 focus:outline-none focus:border-[#D4AF37]/40 transition-colors resize-none" />
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    data-testid="inquiry-add-item"
                    onClick={addItem}
                    className="w-full border border-dashed border-[#D4AF37]/30 hover:border-[#D4AF37]/60 hover:bg-[#D4AF37]/5 text-[#D4AF37] text-xs tracking-widest uppercase font-semibold py-3 rounded-sm transition-all"
                  >
                    + Add Another Item
                  </button>
                </div>
                {error && <p data-testid="inquiry-error" className="text-red-400 text-sm">{error}</p>}
                <button data-testid="inquiry-submit" type="submit" disabled={submitting} className="w-full bg-[#722F37] hover:bg-[#8B3A44] disabled:opacity-50 text-white py-4 rounded-sm text-sm font-semibold tracking-widest uppercase transition-colors flex items-center justify-center gap-2">
                  {submitting ? <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Sending...</> : <><Send className="w-4 h-4" />Send Appointment Request</>}
                </button>
                <p className="text-center text-xs text-gray-700">Or call: <a href="tel:9871803080" className="text-[#D4AF37] hover:underline">+91 98718 03080</a></p>
              </form>
            )}
          </ScrollReveal>

          <div className="lg:col-span-2 space-y-4">
            <ScrollReveal delay={0.2}>
              <div className="bg-gradient-to-br from-[#722F37]/10 to-transparent border border-[#722F37]/20 rounded-sm p-6">
                <h3 style={{ fontFamily: "Georgia, serif" }} className="text-lg font-bold text-white mb-5">Visit Our Studio</h3>
                <div className="space-y-4">
                  {[
                    { href: "tel:9871803080", icon: <Phone className="w-4 h-4 text-[#D4AF37]" />, label: "Phone", value: "+91 98718 03080" },
                    { href: "#", icon: <MapPin className="w-4 h-4 text-[#D4AF37]" />, label: "Address", value: "St Mary's Convent School Road, Gajraula, Amroha UP 244235" },
                    { href: "#", icon: <Clock className="w-4 h-4 text-[#D4AF37]" />, label: "Hours", value: "9:30 AM – 9:00 PM · Daily" },
                  ].map(({ href, icon, label, value }, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-sm bg-[#722F37]/20 flex items-center justify-center flex-shrink-0">{icon}</div>
                      <div><p className="text-[10px] text-gray-600 uppercase tracking-wider">{label}</p><p className="text-white text-sm font-medium leading-relaxed">{value}</p></div>
                    </div>
                  ))}
                </div>
              </div>
            </ScrollReveal>
            <ScrollReveal delay={0.3}>
              <a href={GOOGLE_URL} target="_blank" rel="noopener noreferrer" className="block bg-[#0f0f0f] border border-[#1a1a1a] hover:border-[#D4AF37]/20 rounded-sm p-4 transition-all group">
                <div className="flex items-center gap-3">
                  <svg className="w-8 h-8 flex-shrink-0" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                  <div><div className="flex items-center gap-2"><Stars /><span className="text-white font-bold text-sm">4.6</span></div><p className="text-gray-600 text-xs mt-0.5 group-hover:text-[#D4AF37] transition-colors">View all 26 Google Reviews →</p></div>
                </div>
              </a>
            </ScrollReveal>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── MAIN ───────────────────────────────────────────────────── */
export default function LandingPage() {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => { document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }); setMenuOpen(false); };

  return (
    <div data-testid="landing-page" className="min-h-screen bg-[#060606] text-white overflow-x-clip" style={{ fontFamily: "'Manrope', system-ui, sans-serif" }}>

      {/* ── WhatsApp Float ── */}
      <a href="https://wa.me/919871803080" target="_blank" rel="noopener noreferrer" data-testid="whatsapp-float"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-[#25D366] rounded-full flex items-center justify-center shadow-2xl hover:scale-110 transition-transform">
        <svg className="w-7 h-7 fill-white" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
      </a>

      {/* ── Navbar ── */}
      <nav className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${scrollY > 60 ? "bg-[#060606]/96 backdrop-blur-xl border-b border-white/5" : "bg-transparent"}`}>
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-3 cursor-pointer min-w-0" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
            <img src={LOGO_URL} alt="VR" className="w-9 h-9 sm:w-10 sm:h-10 rounded-full object-cover ring-2 ring-[#D4AF37]/30 flex-shrink-0" />
            <div className="min-w-0">
              <p style={{ fontFamily: "Georgia, serif" }} className="font-bold text-[13px] sm:text-sm text-white leading-tight truncate">VR The Darzi Studio</p>
              <p className="text-[9px] sm:text-[10px] text-[#D4AF37] tracking-[0.2em] uppercase truncate">Gajraula · Amroha</p>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-7">
            {[["Services","services"],["Our Story","story"],["Reviews","reviews"],["Contact","book"]].map(([l, id]) => (
              <button key={id} onClick={() => scrollTo(id)} className="text-sm text-gray-500 hover:text-[#D4AF37] transition-colors tracking-wide">{l}</button>
            ))}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button onClick={() => scrollTo("book")} className="hidden sm:flex items-center gap-2 bg-[#722F37] hover:bg-[#8B3A44] text-white text-sm px-5 py-2 rounded-sm transition-all">
              <Scissors className="w-4 h-4" /> Book Now
            </button>
            <button data-testid="admin-portal-btn" onClick={() => navigate("/login")} className="hidden sm:inline-flex border border-white/10 hover:border-[#D4AF37]/40 text-gray-500 hover:text-[#D4AF37] text-xs px-4 py-2 rounded-sm transition-all">Admin</button>
            <button className="md:hidden p-2 -mr-2 text-gray-300" onClick={() => setMenuOpen(o => !o)} aria-label="Menu">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {menuOpen ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /> : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />}
              </svg>
            </button>
          </div>
        </div>
        {menuOpen && (
          <div className="md:hidden bg-[#060606]/98 backdrop-blur-xl border-t border-white/5 px-4 py-4 space-y-1">
            {[["Services","services"],["Our Story","story"],["Reviews","reviews"],["Book Appointment","book"]].map(([l, id]) => (
              <button key={id} onClick={() => scrollTo(id)} className="block w-full text-left text-sm text-gray-400 hover:text-[#D4AF37] transition-colors py-2.5">{l}</button>
            ))}
            <button onClick={() => { setMenuOpen(false); navigate("/login"); }} className="block w-full text-left text-sm text-gray-500 hover:text-[#D4AF37] transition-colors py-2.5 border-t border-white/5 mt-2 pt-3">Admin Portal</button>
          </div>
        )}
      </nav>

      {/* ── HERO ── */}
      <section className="relative min-h-screen flex items-center overflow-hidden pt-20 pb-10">
        <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover" style={{ filter: "brightness(0.18) saturate(0.4)", transform: `translateY(${scrollY * 0.3}px)` }} />
        <div className="absolute inset-0 bg-gradient-to-b from-[#060606]/70 via-transparent to-[#060606]" />
        <div className="absolute inset-0" style={{ backgroundImage: "radial-gradient(ellipse 70% 60% at 50% 50%, rgba(114,47,55,0.18) 0%, transparent 70%)" }} />

        {/* Side info panel — desktop only */}
        <div className="absolute right-6 top-1/2 -translate-y-1/2 hidden lg:flex flex-col items-end gap-7 text-right z-10">
          <div>
            <p className="editorial-label mb-1.5"><span className="paren">(</span>CURRENTLY<span className="paren">)</span></p>
            <p className="text-xs text-gray-400">Open · 9:30 AM – 9 PM</p>
          </div>
          <div>
            <p className="editorial-label mb-1.5"><span className="paren">(</span>LOCATED IN<span className="paren">)</span></p>
            <p className="text-xs text-gray-400">Gajraula, Amroha</p>
            <p className="text-xs text-gray-400">Uttar Pradesh</p>
          </div>
          <div>
            <p className="editorial-label mb-1.5"><span className="paren">(</span>RATED<span className="paren">)</span></p>
            <div className="flex justify-end gap-0.5 mb-1"><Stars /></div>
            <p className="text-xs text-gray-400">4.7 · 31 Google Reviews</p>
          </div>
        </div>

        {/* Hero content */}
        <div className="relative z-10 px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto w-full">
          {/* Top mini-meta row */}
          <div className="flex items-center gap-3 mb-8 sm:mb-10">
            <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] animate-pulse" />
            <SectionLabel>BESPOKE TAILORING · GAJRAULA</SectionLabel>
          </div>

          {/* MASSIVE display heading */}
          <h1 className="display-tighter text-white mb-6 sm:mb-8" style={{ fontSize: "clamp(56px, 13vw, 188px)" }}>
            <RevealText delay={0.05} className="block">Stitched.</RevealText>
            <RevealText delay={0.18} className="block text-gray-400">Crafted.</RevealText>
            <RevealText delay={0.32} className="block shine-text italic" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 600, letterSpacing: "-0.02em" }}>Perfected.</RevealText>
          </h1>

          <ScrollReveal delay={0.55}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-8 max-w-4xl">
              <div className="md:col-span-2">
                <p className="text-gray-400 text-sm sm:text-base leading-relaxed font-display font-light">
                  Where tradition meets craft. Every garment is a story — your story —
                  told in fabric, stitch by precise stitch since the masters set up shop in Gajraula.
                </p>
              </div>
              <div className="hidden md:flex items-end justify-end">
                <p className="editorial-label text-[10px]"><span className="paren">(</span>EST<span className="paren">)</span> <span className="text-gray-500">2018</span></p>
              </div>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.7}>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 mt-8 sm:mt-12">
              <button onClick={() => scrollTo("book")}
                className="group bg-[#722F37] hover:bg-[#8B3A44] text-white px-6 sm:px-8 py-4 text-xs sm:text-sm font-display font-semibold tracking-[0.2em] uppercase transition-all flex items-center justify-center gap-3 rounded-sm">
                <Scissors className="w-4 h-4" />
                Book Appointment
                <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
              <a href="tel:9871803080" className="border border-[#D4AF37]/40 hover:border-[#D4AF37] hover:bg-[#D4AF37]/5 text-[#D4AF37] px-6 sm:px-8 py-4 text-xs sm:text-sm font-display font-semibold tracking-[0.2em] uppercase transition-all flex items-center justify-center gap-2 rounded-sm">
                <Phone className="w-4 h-4" /> +91 98718 03080
              </a>
            </div>
          </ScrollReveal>
        </div>

        {/* Scroll hint */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2" style={{ animation: "arrowBounce 2s ease-in-out infinite" }}>
          <p className="editorial-label text-[9px]"><span className="paren">(</span>SCROLL<span className="paren">)</span></p>
          <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 9l-7 7-7-7" /></svg>
        </div>
      </section>

      {/* ── MARQUEE ── */}
      <section className="py-4 bg-[#722F37] overflow-hidden border-y border-[#8B3A44]">
        <div className="flex gap-0 animate-[marquee_30s_linear_infinite] whitespace-nowrap" style={{ width: "max-content" }}>
          {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map((item, i) => (
            <span key={i} className="inline-flex items-center gap-4 px-8 text-white/90 text-sm font-medium tracking-widest uppercase">
              {item}<span className="text-[#D4AF37] text-lg">✦</span>
            </span>
          ))}
        </div>
      </section>

      {/* ── STATS ── */}
      <section className="py-16 sm:py-20 px-4 bg-[#060606] border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="mb-10 flex items-center justify-between">
            <SectionLabel>NUMBERS DON'T LIE</SectionLabel>
            <div className="hidden sm:block w-32 h-[1px] bg-gradient-to-r from-[#722F37]/0 via-[#D4AF37]/40 to-[#D4AF37]/0" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10">
            {[{ end: 25000, suffix: "+", label: "Happy Customers" }, { end: 6, suffix: "+", label: "Years of Excellence" }, { end: 31, suffix: "", label: "Google Reviews" }, { end: 100, suffix: "%", label: "Custom Made" }].map(({ end, suffix, label }, i) => (
              <ScrollReveal key={i} delay={i * 0.08}>
                <div className="numeral-massive text-white text-5xl sm:text-6xl md:text-7xl mb-2"><Counter end={end} suffix={suffix} /></div>
                <div className="text-gray-500 text-xs sm:text-sm font-display tracking-wide">{label}</div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── SERVICES ── */}
      <section id="services" className="py-20 sm:py-28 px-4 bg-[#040404]">
        <div className="max-w-7xl mx-auto">
          <div className="mb-14 sm:mb-16">
            <ScrollReveal>
              <SectionLabel className="mb-5">SERVICES</SectionLabel>
              <h2 className="display-tight text-white text-4xl sm:text-6xl md:text-7xl lg:text-8xl">
                <span className="block">Our Bespoke</span>
                <span className="block shine-text italic" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 600, letterSpacing: "-0.02em" }}>Expertise.</span>
              </h2>
            </ScrollReveal>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
            {SERVICES.map((s, i) => {
              const num = String(i + 1).padStart(2, "0");
              return (
                <ScrollReveal key={i} delay={i * 0.08}>
                  <div className="group relative h-72 sm:h-80 rounded-sm overflow-hidden cursor-default">
                    <img src={s.img} alt={s.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" style={{ filter: "brightness(0.45) saturate(0.75)" }} />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/30 to-black/10" />
                    {/* Massive numeral overlay */}
                    <div className="numeral-massive absolute top-4 left-5 text-white/15 text-7xl sm:text-8xl pointer-events-none select-none">{num}</div>
                    <div className="absolute top-0 right-0 w-1 h-0 bg-gradient-to-b from-[#D4AF37] to-[#722F37] group-hover:h-full transition-all duration-700" />
                    <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-6 translate-y-1 group-hover:translate-y-0 transition-transform duration-300">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="editorial-label text-[10px]"><span className="paren">(</span>{num}<span className="paren">)</span></span>
                        <div className="w-8 h-[1px] bg-[#D4AF37]/50" />
                      </div>
                      <h3 className="display-tight text-white text-2xl sm:text-3xl mb-1.5">{s.name}</h3>
                      <p className="text-gray-400 text-xs sm:text-sm leading-relaxed opacity-80 group-hover:opacity-100 transition-opacity duration-300">{s.desc}</p>
                    </div>
                  </div>
                </ScrollReveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── BRAND MANIFESTO ── */}
      <section id="story" className="py-24 sm:py-32 px-4 bg-[#060606] relative overflow-hidden">
        <div className="absolute inset-0" style={{ backgroundImage: "radial-gradient(ellipse 60% 60% at 50% 50%, rgba(114,47,55,0.08) 0%, transparent 70%)" }} />
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <ScrollReveal><SectionLabel className="mb-8">OUR BELIEF</SectionLabel></ScrollReveal>
          <div className="display-tight text-white space-y-1" style={{ fontSize: "clamp(28px, 5vw, 64px)" }}>
            <RevealText delay={0.1} className="block">Fabric is just the beginning.</RevealText>
            <RevealText delay={0.3} className="block text-gray-500">Measurement is the language.</RevealText>
            <RevealText delay={0.5} className="block text-gray-600">Craft is the craft.</RevealText>
            <RevealText delay={0.7} className="block shine-text italic" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 600, letterSpacing: "-0.02em" }}>You are the masterpiece.</RevealText>
          </div>
        </div>
      </section>

      {/* ── STICKY STORY PROCESS ── */}
      <StoryProcess />

      {/* ── JOURNEY MAP ── */}
      <JourneyMap />

      {/* ── CRAFT IMAGE SPLIT ── */}
      <section className="overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-2">
          <div className="relative min-h-[380px] lg:min-h-[560px]">
            <img src={CRAFT_IMG} alt="Tailoring craft" className="w-full h-full object-cover" style={{ filter: "brightness(0.5) saturate(0.7)" }} />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#060606]" />
          </div>
          <div className="bg-[#060606] flex items-center px-6 sm:px-10 md:px-16 py-16 sm:py-20">
            <ScrollReveal>
              <SectionLabel className="mb-5">OUR PROMISE</SectionLabel>
              <h2 className="display-tight text-white mb-6" style={{ fontSize: "clamp(32px, 4.5vw, 64px)" }}>
                <span className="block">Fabric. Fashion.</span>
                <span className="block">Fit. <span className="shine-text italic" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 600, letterSpacing: "-0.02em" }}>Finesse.</span></span>
              </h2>
              <p className="text-gray-500 leading-relaxed mb-7 text-sm sm:text-base font-display font-light max-w-md">At VR The Darzi Studio, we believe clothing is not just fabric — it is identity. Each piece is handcrafted by master tailors who understand that the perfect fit changes how you feel and how the world sees you.</p>
              <ul className="space-y-3">
                {["Premium quality fabrics from top suppliers","Expert craftsmen with decades of experience","22+ precision measurements per garment","Timely delivery, always"].map((p, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-gray-400 font-display">
                    <CheckCircle className="w-4 h-4 text-[#D4AF37] mt-0.5 flex-shrink-0" />{p}
                  </li>
                ))}
              </ul>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* ── GOOGLE REVIEWS ── */}
      <section id="reviews" className="py-20 sm:py-28 bg-[#040404] overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 mb-12 sm:mb-14">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <ScrollReveal>
              <SectionLabel className="mb-5">TESTIMONIALS</SectionLabel>
              <h2 className="display-tight text-white text-4xl sm:text-6xl md:text-7xl">
                <span className="block">What Our</span>
                <span className="block shine-text italic" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 600, letterSpacing: "-0.02em" }}>Customers Say.</span>
              </h2>
            </ScrollReveal>
            <a href={GOOGLE_URL} target="_blank" rel="noopener noreferrer" data-testid="google-reviews-link"
              className="inline-flex items-center gap-3 bg-[#0f0f0f] border border-[#1a1a1a] hover:border-[#D4AF37]/30 rounded-sm px-5 py-3 transition-all self-start md:self-auto">
              <svg className="w-6 h-6 flex-shrink-0" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
              <div><div className="flex items-center gap-2"><Stars /><span className="font-bold text-white text-sm font-display">4.7 / 5</span></div><p className="text-gray-600 text-xs mt-0.5 font-display">31 Google Reviews · See All</p></div>
            </a>
          </div>
        </div>
        <div className="flex gap-5 animate-[marquee_40s_linear_infinite] whitespace-nowrap" style={{ width: "max-content" }}>
          {[...REVIEWS, ...REVIEWS].map((r, i) => (
            <div key={i} className="inline-block w-80 bg-[#0f0f0f] border border-[#1a1a1a] rounded-sm p-6 align-top whitespace-normal flex-shrink-0">
              <div className="flex items-start justify-between mb-3">
                <div className="w-10 h-10 rounded-full bg-[#722F37]/30 flex items-center justify-center text-[#D4AF37] font-bold text-base flex-shrink-0">{r.name[0]}</div>
                <svg className="w-5 h-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
              </div>
              <Stars n={r.rating} />
              <p className="text-gray-400 text-sm leading-relaxed mt-3 mb-3 font-display font-light">"{r.text}"</p>
              <div><p className="text-white text-sm font-semibold font-display">{r.name}</p><p className="text-gray-600 text-xs font-display">{r.date}</p></div>
            </div>
          ))}
        </div>
      </section>

      {/* ── FAQ (visible on-page, mirrors FAQPage JSON-LD for AEO/GEO) ── */}
      <section id="faq" className="py-20 sm:py-28 px-4 bg-[#040404] border-t border-white/5">
        <div className="max-w-4xl mx-auto">
          <ScrollReveal className="mb-12 sm:mb-14">
            <SectionLabel className="mb-5">FAQ</SectionLabel>
            <h2 className="display-tight text-white" style={{ fontSize: "clamp(36px, 6vw, 80px)" }}>
              <span className="block">Frequently</span>
              <span className="block shine-text italic" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 600, letterSpacing: "-0.02em" }}>Asked.</span>
            </h2>
          </ScrollReveal>
          <div className="space-y-3">
            {FAQS.map((f, i) => (
              <ScrollReveal key={i} delay={i * 0.05}>
                <FaqItem q={f.q} a={f.a} num={String(i + 1).padStart(2, "0")} />
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── BIG CTA ── */}
      <section className="py-20 sm:py-28 px-4 bg-[#060606] relative overflow-hidden">
        <div className="absolute inset-0 opacity-30" style={{ backgroundImage: "radial-gradient(ellipse 70% 70% at 50% 50%, rgba(114,47,55,0.18) 0%, transparent 70%)" }} />
        <div className="max-w-7xl mx-auto relative z-10">
          <ScrollReveal>
            <SectionLabel className="mb-6">GET IN TOUCH</SectionLabel>
          </ScrollReveal>
          <h2 className="display-tighter text-white text-center" style={{ fontSize: "clamp(48px, 11vw, 156px)" }}>
            <RevealText delay={0.1} className="block">Let's Make</RevealText>
            <RevealText delay={0.25} className="block shine-text italic" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 600, letterSpacing: "-0.02em" }}>Something</RevealText>
            <RevealText delay={0.4} className="block">Together.</RevealText>
          </h2>
          <ScrollReveal delay={0.6}>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 sm:gap-4 mt-12 sm:mt-16">
              <button onClick={() => scrollTo("book")}
                className="group bg-[#D4AF37] hover:bg-[#E6C04C] text-black px-7 sm:px-10 py-4 text-xs sm:text-sm font-display font-bold tracking-[0.25em] uppercase transition-all flex items-center justify-center gap-3 rounded-sm">
                Book Appointment
                <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
              <a href="https://wa.me/919871803080" target="_blank" rel="noopener noreferrer"
                className="border border-white/15 hover:border-[#D4AF37]/60 text-white px-7 sm:px-10 py-4 text-xs sm:text-sm font-display font-semibold tracking-[0.25em] uppercase transition-all flex items-center justify-center gap-2 rounded-sm">
                <MessageCircle className="w-4 h-4 text-[#25D366]" /> WhatsApp Us
              </a>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ── BOOKING FORM ── */}
      <BookingForm />

      {/* ── FOOTER ── */}
      <footer className="border-t border-white/5 py-10 px-4 bg-[#030303]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img src={LOGO_URL} alt="VR" className="w-10 h-10 rounded-full object-cover ring-2 ring-[#D4AF37]/20" />
                <div><p style={{ fontFamily: "Georgia, serif" }} className="font-bold text-white text-sm">VR The Darzi Studio</p><p className="text-xs text-[#D4AF37] tracking-widest">Bespoke Tailoring</p></div>
              </div>
              <p className="text-gray-700 text-xs leading-relaxed">Gajraula's trusted tailoring studio for custom men's clothing — suits, sherwani, kurta, and more.</p>
            </div>
            <div>
              <p className="text-xs text-gray-600 uppercase tracking-widest mb-3">Quick Links</p>
              <div className="space-y-2">
                {[["Services","services"],["Our Story","story"],["Reviews","reviews"],["Book Appointment","book"]].map(([l, id]) => (
                  <button key={id} onClick={() => scrollTo(id)} className="block text-sm text-gray-600 hover:text-[#D4AF37] transition-colors">{l}</button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs text-gray-600 uppercase tracking-widest mb-3">Connect</p>
              <div className="space-y-2">
                <a href="tel:9871803080" className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#D4AF37] transition-colors"><Phone className="w-3.5 h-3.5" /> +91 98718 03080</a>
                <a href="https://wa.me/919871803080" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#25D366] transition-colors"><MessageCircle className="w-3.5 h-3.5" /> WhatsApp Us</a>
                <a href="https://www.instagram.com/vrthedarzi/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-gray-600 hover:text-pink-400 transition-colors"><Instagram className="w-3.5 h-3.5" /> @vrthedarzi</a>
              </div>
            </div>
          </div>
          <div className="border-t border-white/5 pt-6 flex flex-col md:flex-row items-center justify-between gap-3">
            <p className="text-xs text-gray-700">© {new Date().getFullYear()} VR The Darzi Studio. All rights reserved.</p>
            <p className="text-xs text-gray-700">St Mary's Convent School Road, Gajraula, Amroha, UP</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
